import { ministryIdForApi } from "./ministryContext";
import { api } from "./axios";

import type {
  GenerateServiceOccurrencePayload,
  ServiceOccurrence,
  ServiceOccurrenceUpdatePayload,
  ServiceOccurrenceCreatePayload,
  ServiceSchedule,
  ServiceScheduleCreatePayload,
} from "@/types/service";

type ServiceOccurrenceApiResponse = {
  id: string;
  church_id: string;
  ministry_id: string | null;
  schedule_id: string | null;

  name: string;
  service_type: string;

  starts_at: string;
  ends_at: string | null;

  location: string | null;
  capacity: number | null;

  status: string;

  created_at?: string;
  updated_at?: string;
};

function mapServiceOccurrence(
  service: ServiceOccurrenceApiResponse
): ServiceOccurrence {
  return {
    id: service.id,
    church_id: service.church_id,
    ministry_id: service.ministry_id,
    schedule_id: service.schedule_id,

    title: service.name,

    service_type: service.service_type,
    starts_at: service.starts_at,
    ends_at: service.ends_at,

    location: service.location,
    capacity: service.capacity,

    status: service.status,

    notes: null,
  };
}

export async function getServiceOccurrences(
  churchId: string, ministryId?: string | null,
  range?: { starts_from: string; starts_before: string }
): Promise<ServiceOccurrence[]> {
  const response = await api.get<
    ServiceOccurrenceApiResponse[]
  >(
    `/api/churches/${churchId}/service-occurrences`, { params: { ministry_id: ministryIdForApi(ministryId), starts_from: range?.starts_from, starts_before: range?.starts_before } }
  );

  return response.data.map(mapServiceOccurrence);
}

export async function getServiceSchedules(
  churchId: string, ministryId?: string | null
): Promise<ServiceSchedule[]> {
  const response = await api.get<ServiceSchedule[]>(
    `/api/churches/${churchId}/service-schedules`, { params: { ministry_id: ministryIdForApi(ministryId) } }
  );

  return response.data;
}

export async function createServiceSchedule(
  churchId: string,
  payload: ServiceScheduleCreatePayload
): Promise<ServiceSchedule> {
  const response = await api.post<ServiceSchedule>(
    `/api/churches/${churchId}/service-schedules`,
    payload
  );

  return response.data;
}

export async function generateServiceOccurrence(
  churchId: string,
  scheduleId: string,
  payload: GenerateServiceOccurrencePayload
): Promise<ServiceOccurrence> {
  const response =
    await api.post<ServiceOccurrenceApiResponse>(
      `/api/churches/${churchId}/service-schedules/${scheduleId}/generate-occurrence`,
      payload
    );

  return mapServiceOccurrence(response.data);
}

export async function updateServiceOccurrence(
  churchId: string,
  occurrenceId: string,
  payload: ServiceOccurrenceUpdatePayload
): Promise<ServiceOccurrence> {
  const apiPayload = {
    ...payload,
    name: payload.name,
  };

  const response =
    await api.patch<ServiceOccurrenceApiResponse>(
      `/api/churches/${churchId}/service-occurrences/${occurrenceId}`,
      apiPayload
    );

  return mapServiceOccurrence(response.data);
}

export async function getServiceOccurrence(churchId: string, occurrenceId: string): Promise<ServiceOccurrence> {
  const response = await api.get<ServiceOccurrenceApiResponse>(`/api/churches/${churchId}/service-occurrences/${occurrenceId}`);
  return mapServiceOccurrence(response.data);
}

export async function deleteServiceOccurrence(churchId: string, occurrenceId: string): Promise<void> {
  await api.delete(`/api/churches/${churchId}/service-occurrences/${occurrenceId}`);
}

export async function updateServiceSchedule(churchId: string, scheduleId: string, payload: Partial<ServiceScheduleCreatePayload>): Promise<ServiceSchedule> {
  return (await api.patch<ServiceSchedule>(`/api/churches/${churchId}/service-schedules/${scheduleId}`, payload)).data;
}

export async function createServiceOccurrence(churchId: string, payload: ServiceOccurrenceCreatePayload): Promise<ServiceOccurrence> {
  const response = await api.post<ServiceOccurrenceApiResponse>(`/api/churches/${churchId}/service-occurrences`, payload);
  return mapServiceOccurrence(response.data);
}
